import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { CreateUpdateLocationFormValues } from "../locationsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import {
    CompanyAdminLocationsQuery,
    LocationForCompanyResponse,
    fetchLocationForCompany,
    updateLocationForCompany,
} from "../locationsApi"
import { CreateUpdateLocationForm } from "../components/CreateUpdateLocationForm"
import { COMPANY_ADMIN_ORGANIZATION_LOCATIONS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"

const selectLocation = (data: LocationForCompanyResponse) => {
    const location = data.location
    if (!location) return null
    const { id, company, serviceCenters, history, ...restValues } = location

    const initialValues: Omit<CreateUpdateLocationFormValues, "shops"> = {
        ...restValues,
    }

    if (restValues.shops && restValues.shops.length > 0) {
        const filteredShops = restValues.shops.map((shop) => shop.id)
        return {
            initialValues: { ...initialValues, shops: filteredShops },
            history,
        }
    }

    return { initialValues, history }
}

const Location = forwardRef<FormikProps<CreateUpdateLocationFormValues>>((props, ref) => {
    const { locationId } = useParams()
    const navigate = useNavigate()

    const locationResult = useApiQuery(fetchLocationForCompany, {
        queryName: CompanyAdminLocationsQuery.DETAILS,
        dependencies: {
            locationId: locationId || "",
        },
        enabled: Boolean(locationId),
        select: selectLocation,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateLocation } = useApiMutation(updateLocationForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            context.queryClient.invalidateQueries({ queryKey: [CompanyAdminLocationsQuery.LIST] })
            context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(CompanyAdminLocationsQuery.DETAILS, { locationId: params.locationId }),
            })
            navigate(COMPANY_ADMIN_ORGANIZATION_LOCATIONS_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateLocationForm
                ref={ref}
                initialValues={locationResult.data?.initialValues}
                history={locationResult.data?.history}
                onSubmit={(data) => {
                    locationId && callUpdateLocation({ location: data, locationId: locationId })
                }}
            />
        </>
    )
})

export function EditLocation() {
    const locationFormRef = useRef<FormikProps<CreateUpdateLocationFormValues>>(null)

    const handleSaveClick = () => {
        if (locationFormRef.current) {
            locationFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="edit-location-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_LOCATIONS_EDIT}
                    title="Edit location"
                    rightElement={
                        <LocalizedStrict id="edit-location-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-locations-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_LOCATIONS_ROUTE}>Locations</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-locations-edit-title">
                            <Breadcrumb>Edit location</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Location ref={locationFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
